<template>
  <footer class="ls-footer">
    <div class="ls-footer__inner">
      <div class="ls-footer__brand">
        <img
          v-if="logo && logo.url && !logoFailed"
          class="ls-footer__logo"
          :src="logo.url"
          :alt="brandName"
          loading="lazy"
          decoding="async"
          @error="logoFailed = true"
        >
        <span
          v-else
          class="ls-footer__wordmark"
        >{{ brandName }}</span>
        <a
          v-if="websiteUrl"
          class="ls-footer__site"
          :href="websiteUrl"
          target="_blank"
          rel="noopener"
        >{{ websiteLabel }}</a>
      </div>
      <p class="ls-footer__mark">
        Shared via <span class="ls-footer__kingpin">Kingpin</span>
        <span aria-hidden="true">/</span>
        <a
          href="https://kingpin.global/privacy-policy.html"
          target="_blank"
          rel="noopener"
        >Privacy</a>
      </p>
    </div>
    <div
      v-if="showCookieRow"
      class="ls-footer__cookies"
      data-test="ls-cookie-row"
    >
      <p>
        This page uses cookies to remember your visit.
        <a
          href="https://kingpin.global/privacy-policy.html"
          target="_blank"
          rel="noopener"
        >Privacy policy</a>
      </p>
      <button
        type="button"
        @click="acceptCookies"
      >Accept</button>
    </div>
  </footer>
</template>

<script>
import { getCookie, setCookies } from '@/common-utils'

/** Same consent cookie and lifetime as the portal's banner (AppCookies). */
const CONSENT_COOKIE = 'EU_COOKIE_LAW_CONSENT'
const CONSENT_DAYS = 30

export default {
  name: 'LsFooter',
  props: {
    brandName: { type: String, required: true },
    website: { type: String, default: '' },
    logo: { type: Object, default: null },
  },
  data() {
    return { logoFailed: false, showCookieRow: getCookie(CONSENT_COOKIE) == null }
  },
  computed: {
    websiteUrl() {
      try {
        return new URL(this.website).protocol === 'https:' ? this.website : ''
      } catch (e) {
        return ''
      }
    },
    websiteLabel() {
      try {
        return new URL(this.websiteUrl).hostname.replace(/^www\./, '')
      } catch (e) {
        return ''
      }
    },
  },
  methods: {
    acceptCookies() {
      setCookies(CONSENT_COOKIE, CONSENT_DAYS)
      this.showCookieRow = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-footer {
  margin-top: clamp(48px, 8vw, 96px);
  border-top: 1px solid var(--ls-line);
  background: var(--ls-surface);
  color: var(--ls-ink);

  &__inner {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: $ls-max;
    margin: 0 auto;
    padding: 40px $ls-gutter;

    @include ls-up($ls-md) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__brand {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 20px;
  }

  &__logo {
    display: block;
    width: auto;
    max-height: 32px;
    max-width: 180px;
  }

  &__wordmark {
    @include ls-heading;
    font-size: 1.25rem;
  }

  &__site,
  &__mark a,
  &__cookies a {
    color: var(--ls-ink);
    text-underline-offset: 3px;
    @include ls-focus;
  }

  &__site {
    font: 400 0.9375rem/1.4 var(--ls-body-font);
  }

  &__mark {
    display: flex;
    gap: 8px;
    margin: 0;
    color: var(--ls-muted);
    font: 400 0.75rem/1.4 var(--ls-body-font);
  }

  // The one Kingpin mark the branded page carries, at 12px.
  &__kingpin {
    color: var(--ls-ink);
    font-weight: 600;
  }

  &__cookies {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    max-width: $ls-max;
    margin: 0 auto;
    padding: 12px $ls-gutter calc(16px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--ls-line);
    color: var(--ls-muted);
    font: 400 0.8125rem/1.5 var(--ls-body-font);

    p {
      margin: 0;
    }

    button {
      min-height: 40px;
      padding: 0 18px;
      border: 1px solid var(--ls-ink);
      border-radius: var(--ls-radius);
      background: transparent;
      color: var(--ls-ink);
      font: 600 0.8125rem/1 var(--ls-body-font);
      cursor: pointer;
      @include ls-focus;
    }
  }
}
</style>
